/**
 * Export utilities for CSV and PDF generation
 */

export interface ExportColumn {
  key: string;
  header: string;
  formatter?: (value: any) => string;
}

/**
 * Export data to CSV format and trigger download
 */
export const exportToCSV = (
  data: any[],
  columns: ExportColumn[],
  filename: string = 'export'
): void => {
  if (!data || data.length === 0) {
    alert('No data to export');
    return;
  }

  // Create header row
  const headers = columns.map((col) => `"${col.header}"`).join(',');

  // Create data rows
  const rows = data.map((item) => {
    return columns
      .map((col) => {
        const value = item[col.key];
        const formatted = col.formatter ? col.formatter(value) : value;
        // Escape quotes and wrap in quotes
        const escaped = String(formatted ?? '').replace(/"/g, '""');
        return `"${escaped}"`;
      })
      .join(',');
  });

  // Combine headers and rows
  const csv = [headers, ...rows].join('\n');

  // Create blob and download
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Export data to PDF format (simple HTML-based PDF generation)
 */
export const exportToPDF = (
  data: any[],
  columns: ExportColumn[],
  title: string = 'Export',
  filename: string = 'export'
): void => {
  if (!data || data.length === 0) {
    alert('No data to export');
    return;
  }

  // Create HTML content for printing
  const tableRows = data
    .map((item) => {
      const cells = columns
        .map((col) => {
          const value = item[col.key];
          const formatted = col.formatter ? col.formatter(value) : value;
          return `<td style="padding: 8px 12px; border-bottom: 1px solid #e5e7eb;">${formatted ?? ''}</td>`;
        })
        .join('');
      return `<tr>${cells}</tr>`;
    })
    .join('');

  const headerCells = columns
    .map((col) => `<th style="padding: 10px 12px; text-align: left; background: #f3f4f6; font-weight: 600; border-bottom: 2px solid #e5e7eb;">${col.header}</th>`)
    .join('');

  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>${title}</title>
      <style>
        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          padding: 20px;
          color: #1f2937;
        }
        .header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          padding-bottom: 20px;
          border-bottom: 2px solid #6366f1;
        }
        h1 {
          margin: 0;
          color: #1f2937;
          font-size: 24px;
        }
        .meta {
          color: #6b7280;
          font-size: 14px;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 16px;
        }
        tr:nth-child(even) {
          background: #f9fafb;
        }
        .footer {
          margin-top: 30px;
          padding-top: 20px;
          border-top: 1px solid #e5e7eb;
          color: #6b7280;
          font-size: 12px;
          text-align: center;
        }
        @media print {
          body { padding: 0; }
          .header { page-break-after: avoid; }
        }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>${title}</h1>
        <div class="meta">
          <div>Generated: ${new Date().toLocaleString()}</div>
          <div>Total Records: ${data.length}</div>
        </div>
      </div>
      <table>
        <thead>
          <tr>${headerCells}</tr>
        </thead>
        <tbody>
          ${tableRows}
        </tbody>
      </table>
      <div class="footer">
        Generated by Trinze Admin Dashboard
      </div>
    </body>
    </html>
  `;

  // Open new window for printing
  const printWindow = window.open('', '_blank');
  if (printWindow) {
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
  }
};

/**
 * Export summary statistics
 */
export const exportSummary = (
  stats: Record<string, any>,
  title: string = 'Summary Report'
): void => {
  const rows = Object.entries(stats).map(([key, value]) => {
    const formattedKey = key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
    return { label: formattedKey, value: String(value) };
  });

  exportToCSV(
    rows,
    [
      { key: 'label', header: 'Metric' },
      { key: 'value', header: 'Value' },
    ],
    title.toLowerCase().replace(/\s+/g, '_')
  );
};

/**
 * Format date for export
 */
export const formatDateForExport = (dateString: string | null | undefined): string => {
  if (!dateString) return 'N/A';
  try {
    return new Date(dateString).toLocaleString();
  } catch {
    return String(dateString);
  }
};

/**
 * Format currency for export
 */
export const formatCurrencyForExport = (
  amount: number | null | undefined,
  currency: string = '₦'
): string => {
  if (amount === null || amount === undefined) return 'N/A';
  return `${currency}${amount.toLocaleString()}`;
};

/**
 * Format percentage for export
 */
export const formatPercentageForExport = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return 'N/A';
  return `${(value * 100).toFixed(2)}%`;
};
